import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { addDays, endOfMonth, format, startOfMonth } from 'date-fns';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COACHES = ['Paul Brailsford', 'Peter Wright'];

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const typeLabel = (t) => {
  if (t === 'leave') return 'Leave';
  if (t === 'day_off') return 'Day Off';
  if (t === 'away_on_course') return 'Away on Course';
  return 'Other';
};

export default function CoachScheduler({ currentUser }) { // ADC Scheduler

  const [monthAnchor, setMonthAnchor] = useState(() => new Date());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [form, setForm] = useState({
    coaches: ['Paul Brailsford'],
    event_type: 'leave',
    title: 'Leave',
    start_datetime: '',
    end_datetime: '',
    notes: '',
  });

  const canEdit = currentUser?.role === 'admin' || currentUser?.role === 'coach';

  const range = useMemo(() => {
    const start = startOfMonth(monthAnchor);
    const end = endOfMonth(monthAnchor);
    return { start, end };
  }, [monthAnchor]);

  useEffect(() => {
    fetchSchedule();
  }, [range.start.toISOString(), range.end.toISOString()]);

  const fetchSchedule = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API}/coach-schedule?start=${encodeURIComponent(range.start.toISOString())}&end=${encodeURIComponent(range.end.toISOString())}`,
        getAuthHeaders()
      );
      setItems(res.data || []);
    } catch (e) {
      toast.error('Failed to load coach schedule');
    } finally {
      setLoading(false);
    }
  };

  const monthDays = useMemo(() => {
    const days = [];
    let d = range.start;
    while (d <= range.end) {
      days.push(d);
      d = addDays(d, 1);
    }
    return days;
  }, [range]);

  const itemsForDayAndCoach = (day, coachName) => {
    const dayKey = format(day, 'yyyy-MM-dd');
    return items
      .filter((it) => (it.coaches || []).includes(coachName))
      .filter((it) => {
        const start = new Date(it.start_datetime);
        return format(start, 'yyyy-MM-dd') === dayKey;
      })
      .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
  };

  const openCreate = () => {
    if (!canEdit) return;
    const start = new Date();
    start.setHours(6, 0, 0, 0);
    const end = new Date();
    end.setHours(14, 0, 0, 0);

    setEditing(null);
    setForm({
      coaches: ['Paul Brailsford'],
      event_type: 'leave',
      title: 'Leave',
      start_datetime: start.toISOString().slice(0, 16),
      end_datetime: end.toISOString().slice(0, 16),
      notes: '',
    });
    setDialogOpen(true);
  };

  const openEdit = (it) => {
    if (!canEdit) return;
    if (it.source !== 'manual') return; // only manual events editable here

    const start = new Date(it.start_datetime);
    const end = new Date(it.end_datetime);

    setEditing(it);
    setForm({
      coaches: it.coaches || [],
      event_type: it.event_type || 'other',
      title: it.title || typeLabel(it.event_type),
      start_datetime: start.toISOString().slice(0, 16),
      end_datetime: end.toISOString().slice(0, 16),
      notes: it.notes || '',
    });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;

    if (!form.coaches.length) {
      toast.error('Select at least one coach');
      return;
    }

    const payload = {
      coaches: form.coaches,
      event_type: form.event_type,
      title: form.title,
      start_datetime: new Date(form.start_datetime).toISOString(),
      end_datetime: new Date(form.end_datetime).toISOString(),
      notes: form.notes,
    };

    try {
      if (editing?.id) {
        await axios.put(`${API}/coach-events/${editing.id}`, payload, getAuthHeaders());
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/coach-events`, payload, getAuthHeaders());
        toast.success('Event created');
      }
      setDialogOpen(false);
      fetchSchedule();
    } catch {
      toast.error('Failed to save event');
    }
  };

  const del = async () => {
    if (!editing?.id) return;
    if (!window.confirm('Delete this event?')) return;

    try {
      await axios.delete(`${API}/coach-events/${editing.id}`, getAuthHeaders());
      toast.success('Event deleted');
      setDialogOpen(false);
      fetchSchedule();
    } catch {
      toast.error('Failed to delete event');
    }
  };

  return (
    <div className="space-y-6" data-testid="coach-scheduler">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-slate-100 mb-2"><span className="text-green-600">ADC</span> Scheduler</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            View ADC availability alongside scheduled assessments, training and DCPC. (Paul Brailsford / Peter Wright)
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setMonthAnchor(addDays(range.start, -1))}>
            Prev
          </Button>
          <Button variant="outline" onClick={() => setMonthAnchor(new Date())}>
            Today
          </Button>
          <Button variant="outline" onClick={() => setMonthAnchor(addDays(range.end, 1))}>
            Next
          </Button>
          {canEdit && <Button onClick={openCreate}>Add Event</Button>}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{format(range.start, 'MMMM yyyy')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-slate-500">Loading…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b dark:border-slate-700">
                    <th className="py-2 pr-3">Date</th>
                    {COACHES.map((c) => (
                      <th key={c} className="py-2 pr-3">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monthDays.map((day) => (
                    <tr key={day.toISOString()} className="border-b dark:border-slate-800 align-top">
                      <td className="py-3 pr-3 whitespace-nowrap">
                        <div className="font-medium text-slate-800 dark:text-slate-200">{format(day, 'EEE dd')}</div>
                      </td>
                      {COACHES.map((coach) => (
                        <td key={coach} className="py-3 pr-3 min-w-[260px]">
                          <div className="space-y-2">
                            {itemsForDayAndCoach(day, coach).length === 0 ? (
                              <div className="text-xs text-slate-400">—</div>
                            ) : (
                              itemsForDayAndCoach(day, coach).map((it) => (
                                <div
                                  key={`${it.source}-${it.id}`}
                                  className={`p-2 rounded border text-xs cursor-pointer hover:shadow-sm transition-all ${
                                    it.source === 'manual'
                                      ? 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700'
                                      : it.source === 'dcpc'
                                        ? 'bg-purple-50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-900'
                                        : 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900'
                                  }`}
                                  onClick={() => openEdit(it)}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="font-medium text-slate-800 dark:text-slate-200 truncate">
                                      {it.source === 'assessment'
                                        ? `${it.assessment_type}${it.course_title ? ` • ${it.course_title}` : ''}`
                                        : it.source === 'dcpc'
                                          ? `DCPC: ${it.module_name}`
                                          : it.title}
                                    </div>
                                    <Badge variant="secondary" className="text-[10px]">
                                      {it.source === 'manual' ? typeLabel(it.event_type) : it.source}
                                    </Badge>
                                  </div>
                                  <div className="text-[10px] text-slate-600 dark:text-slate-400 mt-1">
                                    {format(new Date(it.start_datetime), 'HH:mm')}–{format(new Date(it.end_datetime), 'HH:mm')}
                                  </div>
                                  {it.source === 'assessment' && it.driver_id && (
                                    <div className="text-[10px] mt-1">
                                      <Link className="text-blue-700 dark:text-blue-400" to={`/drivers/${it.driver_id}`}>
                                        Open driver →
                                      </Link>
                                    </div>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[95vw] sm:max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit ADC Event' : 'Add ADC Event'}</DialogTitle>
            <DialogDescription>
              Manual events (Leave/Day Off/Away on Course/Other). Assessments/DCPC appear automatically.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>ADCs</Label>
              <div className="flex gap-4">
                {COACHES.map((c) => (
                  <label key={c} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.coaches.includes(c)}
                      onChange={(e) => {
                        if (e.target.checked) setForm((f) => ({ ...f, coaches: [...f.coaches, c] }));
                        else setForm((f) => ({ ...f, coaches: f.coaches.filter((x) => x !== c) }));
                      }}
                    />
                    {c}
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <select
                  className="w-full px-3 py-2 border dark:border-slate-700 rounded-md"
                  value={form.event_type}
                  onChange={(e) => {
                    const t = e.target.value;
                    setForm((f) => ({ ...f, event_type: t, title: typeLabel(t) }));
                  }}
                >
                  <option value="leave">Leave</option>
                  <option value="day_off">Day Off</option>
                  <option value="away_on_course">Away on Course</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Title</Label>
                <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start</Label>
                <Input
                  type="datetime-local"
                  value={form.start_datetime}
                  onChange={(e) => setForm((f) => ({ ...f, start_datetime: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>End</Label>
                <Input
                  type="datetime-local"
                  value={form.end_datetime}
                  onChange={(e) => setForm((f) => ({ ...f, end_datetime: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>

            <div className="flex justify-between">
              <div>
                {editing && (
                  <Button type="button" variant="outline" onClick={del}>
                    Delete
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Save</Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
